import "./Logo.css";

interface LogoProps {
  size?: number;
  showTagline?: boolean;
  className?: string;
  /**
   * Invert the wordmark for a dark background.
   *
   * The GOLD accent is deliberately not inverted — on deep green it is 7.94:1,
   * versus 1.76:1 on white. That is the entire reason the header bar went dark:
   * it lets the brand colour be the brand colour instead of a documented
   * accessibility exception.
   */
  onDark?: boolean;
}

/**
 * Intrinsic size of `public/easyfarm-mark.png`, which `scripts/derive-logo-assets.mjs`
 * emits at a fixed height. Declared here so the <img> reserves the right box and the
 * header does not reflow once the image decodes.
 */
const MARK_W = 218;
const MARK_H = 176;

/**
 * The EasyFarm shell mark. Height is the constrained dimension and width follows
 * from the artwork's own aspect — never stretch it to fill a fixed-width box.
 *
 * This draws the DERIVED mark, not the file the client sent. That original is the
 * full lockup — illustration plus wordmark plus three strap-lines — with the
 * transparency checkerboard baked into its pixels. Rendering it here did two things
 * at once: it put a pale grey box on the green header, and it squeezed the entire
 * lockup into 33x44px beside a wordmark that already says "EasyFarm" in live
 * text. See scripts/derive-logo-assets.mjs for the derivation and docs/brand/README.md
 * for what still needs Christy (OPEN-QUESTIONS Q4, vector artwork).
 */
export function Logo({
  size = 40,
  showTagline = false,
  className,
  onDark = false,
}: LogoProps) {
  return (
    <div
      className={`logo${onDark ? " logo--on-dark" : ""}${className ? ` ${className}` : ""}`}
    >
      {/*
        Decorative. The wordmark beside it already carries the name, and an
        alt of "EasyFarm" here made a screen reader announce the brand twice.
      */}
      <img
        src="/easyfarm-mark.png"
        alt=""
        aria-hidden="true"
        height={size}
        width={Math.round((size * MARK_W) / MARK_H)}
        className="logo__mark"
      />
      <div className="logo__text">
        {/*
          role="img" + aria-label makes this what it actually is: a LOGOTYPE that
          happens to be rendered as live text rather than a picture.

          That is not a trick to silence a checker. WCAG 1.4.3 exempts "text that
          is part of a logo or brand name" from contrast minimums, which is why
          the gold "Farm" is allowed to be gold — it is gold because the client's
          actual logo is. But axe cannot infer intent from a <span>, so it flagged
          the accent at 1.76:1 as a serious violation. Declaring the role states
          the intent in markup, announces the brand once and correctly, and stops
          the false positive at its source instead of suppressing the rule.
        */}
        <span className="logo__name" role="img" aria-label="EasyFarm">
          <span aria-hidden="true">
            Easy<span className="logo__name-accent">Farm</span>
          </span>
        </span>
        {showTagline && (
          <span className="logo__tagline">Supporting Farmers and Ranchers since 1983</span>
        )}
      </div>
    </div>
  );
}
